import { useEffect, useState } from "react";
import { Loader2, Mail, NotebookPen, Printer } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/checkins";
import { printPdfBlob } from "@/lib/printPdf";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ClientPicker, ResourcePicker } from "./SendPickers";
import { ProgressReviewFields, emptyProgressReview, progressReviewBody } from "./ProgressReviewFields";
import { errText, fetchBlob, isImage, isPdf, useResourceMeta } from "./resourceApi";

const firstAddress = (email) => (email || "").split(/[,;]/)[0].trim();

function announceProgressReview(pr) {
  if (pr.emailed_now) toast.success("Diary started — private link emailed", { description: "Progress Review sends the mid-week reminder and flags you when it's done." });
  else if (pr.email_error) toast.error("Diary started, but the email wasn't sent", { description: `${pr.email_error} — use "Email link" or "Complete with client" on the client's Progress Review tab.` });
  else toast.success("Diary scheduled", { description: `The private link is emailed at 9:00 am on ${fmtDate(pr.start_date)}.` });
}

function MethodButton({ active, onClick, icon: Icon, title, hint, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId} aria-pressed={active}
            className={`flex-1 rounded-lg border p-3 text-left transition-colors ${active ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:bg-zinc-50"}`}>
      <div className="flex items-center gap-2 text-sm font-medium"><Icon className="h-4 w-4" /> {title}</div>
      <div className="mt-0.5 text-xs text-zinc-500">{hint}</div>
    </button>
  );
}

async function printAssignment(a) {
  const blob = await fetchBlob(`/resource-assignments/${a.id}/file`, { preview: true });
  if (isPdf(blob.type) || isPdf(a.content_type)) return printPdfBlob(new Blob([blob], { type: "application/pdf" }));
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return null;
}

/** "Add / Send Clinical Resource" — picks client + approved resource, then print or private-link email. */
export function SendResourceDialog({ client: presetClient, resource: presetResource, source, onClose, onSent }) {
  const meta = useResourceMeta();
  const [client, setClient] = useState(presetClient || null);
  const [resource, setResource] = useState(presetResource || null);
  const [method, setMethod] = useState("email");
  const [recipient, setRecipient] = useState(firstAddress(presetClient?.email));
  const [instructions, setInstructions] = useState("");
  const [reviewDate, setReviewDate] = useState("");
  const [providedNow, setProvidedNow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [opts, setOpts] = useState(null);
  const [pr, setPr] = useState(emptyProgressReview);
  const noEmail = !client?.email || client.email.endsWith("@no-email.local");

  useEffect(() => {
    setOpts(null);
    if (!resource) return;
    api.get(`/clinical-resources/${resource.id}/delivery-options`)
      .then((r) => { setOpts(r.data); setMethod(r.data.methods[0] || null); })
      .catch(() => setOpts({ methods: ["email", "print"] }));
  }, [resource]);

  function pickClient(c) { setClient(c); setRecipient(firstAddress(c.email)); }

  async function submit() {
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${encodeURIComponent(client.email)}/resource-assignments`, {
        resource_id: resource.id, method, canonical_client_id: client.canonicalId || null, client_name: client.name || null,
        recipient_email: method === "email" ? recipient.trim() : null, instructions, review_date: (method !== "progress_review" && reviewDate) || null,
        provided_now: method === "print" && providedNow, source_kind: source?.kind, source_id: source?.id,
        progress_review: method === "progress_review" ? progressReviewBody(pr) : null,
      });
      if (method === "progress_review") {
        announceProgressReview(data.progress_review);
      } else if (method === "print") {
        printAssignment(data).catch((e) => toast.error("Recorded, but printing didn't open", { description: errText(e) }));
        toast.success("Recorded on the client's record", { description: providedNow ? "Marked as given to the client." : "Mark it as given once the client has it." });
      } else if (data.delivery_status === "sent") {
        toast.success(`Private link emailed to ${data.recipient}`, { description: "Recorded on the client's record. The link expires in 30 days." });
      } else {
        toast.error("The email wasn't sent", { description: `${data.delivery_error || "Email delivery failed"} — recorded as not sent.` });
      }
      window.dispatchEvent(new CustomEvent("hr-refresh"));
      onSent?.(data);
      onClose();
    } catch (e) {
      toast.error("Could not send resource", { description: errText(e) });
    } finally {
      setBusy(false);
    }
  }

  const step = !client ? "client" : !resource ? "resource" : "delivery";
  const ready = resource && client && opts && (
    method === "print" || (method === "progress_review" && pr.consent.given)
    || (method === "email" && /^[^@\s,;]+@[^@\s,;]+\.[^@\s,;]+$/.test(recipient.trim())));
  const has = (m) => opts?.methods.includes(m);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="send-resource-dialog">
        <DialogHeader>
          <DialogTitle>Send clinical resource</DialogTitle>
          <DialogDescription>
            {client ? <>For <b>{client.name || client.email}</b>{!presetClient && <button type="button" className="ml-2 text-xs underline" onClick={() => setClient(null)}>change</button>}</> : "Choose the client first."}
            {resource && <> · <b>{resource.title}</b> v{resource.current_version_number}{!presetResource && <button type="button" className="ml-2 text-xs underline" onClick={() => setResource(null)}>change</button>}</>}
          </DialogDescription>
        </DialogHeader>
        {step === "client" && <ClientPicker onPick={pickClient} />}
        {step === "resource" && (meta ? <ResourcePicker meta={meta} onPick={setResource} /> : <Loader2 className="h-4 w-4 animate-spin" />)}
        {step === "delivery" && !opts && <Loader2 className="h-4 w-4 animate-spin text-zinc-400" data-testid="send-options-loading" />}
        {step === "delivery" && opts && !opts.methods.length && (
          <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="send-no-methods">
            {opts.reason || (opts.digital && !opts.digital.published ? "The linked Progress Review template isn't published yet." : "This resource can't be sent to a client from here.")}
          </p>
        )}
        {step === "delivery" && opts?.methods.length > 0 && (
          <div className="space-y-4" data-testid="send-delivery-step">
            <div className="flex gap-2">
              {has("progress_review") && (
                <MethodButton active={method === "progress_review"} onClick={() => setMethod("progress_review")} icon={NotebookPen}
                              title={opts.digital?.kind === "diary" ? `Start ${opts.digital.diary_days}-day diary` : "Start Progress Review"}
                              hint="One private link, reminders and flags" testId="send-method-progress-review" />
              )}
              {has("email") && <MethodButton active={method === "email"} onClick={() => setMethod("email")} icon={Mail} title="Secure email"
                            hint="Private download link, expires in 30 days" testId="send-method-email" />}
              {has("print") && <MethodButton active={method === "print"} onClick={() => setMethod("print")} icon={Printer} title="Print"
                            hint="Opens the print dialog now" testId="send-method-print" />}
            </div>
            {method === "progress_review" && <ProgressReviewFields value={pr} onChange={setPr} digital={opts.digital} />}
            {method === "email" && (
              <div><Label className="text-xs">Send to</Label>
                <Input className="mt-1" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="client@example.com"
                       data-testid="send-recipient-input" />
                {noEmail && <div className="mt-1 text-xs text-amber-700">This client has no email on file — enter one or choose Print.</div>}</div>
            )}
            {method !== "progress_review" && (
              <div><Label className="text-xs">Personalised instructions (optional — included in the email and the record)</Label>
                <Textarea className="mt-1" rows={3} value={instructions} onChange={(e) => setInstructions(e.target.value)}
                          data-testid="send-instructions-input" /></div>
            )}
            {method !== "progress_review" && (
            <div className="flex flex-wrap items-end gap-4">
              <div><Label className="text-xs">Review with client on (optional)</Label>
                <Input type="date" className="mt-1 w-44" value={reviewDate} onChange={(e) => setReviewDate(e.target.value)}
                       data-testid="send-review-date-input" /></div>
              {method === "print" && (
                <label className="flex items-center gap-2 pb-2 text-sm">
                  <Checkbox checked={providedNow} onCheckedChange={(v) => setProvidedNow(!!v)} data-testid="send-provided-now" />
                  Given to the client now
                </label>
              )}
            </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="send-cancel">Cancel</Button>
          {step === "delivery" && opts?.methods.length > 0 && (
            <Button onClick={submit} disabled={!ready || busy} data-testid="send-submit">
              {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              {method === "print" ? "Record & print" : method === "progress_review" ? "Start diary" : "Send email"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SendResourceButton({ client, source, size = "sm", variant = "outline", className = "", testId = "send-resource-btn", label = "Send resource" }) {
  const [open, setOpen] = useState(false);
  const meta = useResourceMeta();
  if (!meta?.perms?.distribute || !client?.email) return null;
  return (
    <>
      <Button size={size} variant={variant} className={className} onClick={() => setOpen(true)} data-testid={testId}>
        <Mail className="h-3.5 w-3.5 mr-1.5" /> {label}
      </Button>
      {open && <SendResourceDialog client={client} source={source} onClose={() => setOpen(false)} />}
    </>
  );
}
